import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import ProjectWizard from './pages/ProjectWizard';
import DockingWorkspace from './pages/DockingWorkspace';
import PopoutShell from './pages/PopoutShell';
import { getPopoutContext } from './lib/popoutContext';
import TitleBar from './components/TitleBar';
import ProjectSidebar from './components/ProjectSidebar';
import ErrorBoundary from './components/ErrorBoundary';
import AuthDialog from './components/AuthDialog';
import SettingsModal from './components/modals/SettingsModal';
import BulkRefreshPill from './components/workspace/BulkRefreshPill';
import AddProjectDialog from './components/modals/AddProjectDialog';
import { ToastContainer } from './components/ui/Toast';
import TerminalCompletionNotifier from './components/terminal/TerminalCompletionNotifier';
import RdpConsentDialog from './components/rdp/RdpConsentDialog';
import StatusFooter from './components/StatusFooter';
import WaitlistScreen from './components/WaitlistScreen';
import { initServerConnection } from './services/serverConnection';
import { useAuthStore } from './stores/authStore';
import { useTerminalStore } from './stores/terminalStore';
import { useUIStore } from './stores/uiStore';
import { useRdpStore } from './stores/rdpStore';
import { useProjectStore } from './stores/projectStore';
import { RDP_ENABLED, BETA_GATE, isBetaInvited } from './config/features';
import { isWeb } from './lib/platform';
import { useMobile } from './hooks/useMobile';
import { logger } from '@/lib/logger';
import { startConfigMigration, stopConfigMigration } from '@/lib/configMigration';
import { getBackend } from '@/services/backend';
import { adoptAllDaemonSessions } from '@/lib/sessionAdopt';

// Capture OIDC code outside the component so React strict mode double-effect
// doesn't lose it (replaceState in the first run clears the URL before the second).
const oidcCode = (() => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (code) {
    window.history.replaceState({}, '', window.location.pathname);
  }
  return code;
})();

function AppContent() {
  const { isAuthenticated, user, checkAuth, setShowAuthDialog } = useAuthStore();
  const sidebarPosition = useUIStore((state) => state.sidebarPosition);
  const pendingConsent = useRdpStore((state) => state.pendingConsent);
  const resolveConsent = useRdpStore((state) => state.resolveConsent);
  const location = useLocation();
  const isMobile = useMobile();

  // Determine if we're on the workspace route (where flexlayout handles the sidebar)
  const isWorkspaceRoute = location.pathname.startsWith('/workspace/');

  // On desktop/web workspace route, hide the App-level ProjectSidebar
  // because flexlayout renders ProjectsPanel as a border panel.
  // On mobile, always show the old sidebar drawer.
  // On Home/Wizard pages, always show the sidebar.
  const showSidebar = isMobile || !isWorkspaceRoute;

  // Initialize app based on platform
  useEffect(() => {
    const initApp = async () => {
      // Restore terminal tabs saved by the pagehide snapshot below, so PTY
      // sessions survive a page reload (the daemon keeps them alive; only the
      // tab map lives in the page). Must run before any workspace mounts its
      // TerminalTab — clearInitializedProjects forces a fresh daemon sync that
      // reconciles restored tabs against sessions that died while we were gone.
      // (This wiring originally lived in the in-app updater flow and was lost
      // when the updater was removed in 0.4.1 — reloads killed terminals.)
      const restored = useTerminalStore.getState().restoreFromPersistence();
      if (restored) {
        logger.info('App', 'Restored terminal sessions from previous page session');
        useTerminalStore.getState().clearInitializedProjects();
      }

      // Handle OIDC callback (Keycloak redirects back with ?code=...)
      // This handles the web flow where the main window is redirected.
      if (oidcCode) {
        const { handleCallback } = useAuthStore.getState();
        const success = await handleCallback(oidcCode);
        if (success) {
          initServerConnection().catch((err) => {
            logger.debug('App', 'Server connection not available:', err);
          });
          return;
        }
      }

      // Check authentication on both platforms
      const authenticated = await checkAuth();

      if (isWeb() && !authenticated) {
        setShowAuthDialog(true);
      } else {
        initServerConnection().catch((err) => {
          logger.debug('App', 'Server connection not available:', err);
        });
      }

      // Start periodic .ateliercode/ config migration manager
      startConfigMigration(getBackend());
    };

    initApp();

    // Snapshot the terminal tab map on page teardown so reloads can restore it.
    // `pagehide` (not just beforeunload) also covers bfcache navigations; the
    // write is synchronous localStorage, safe in an unload handler. The PTYs
    // themselves live in the daemon and don't need persisting.
    const persistTabs = () => useTerminalStore.getState().saveSessionsToStorage();
    window.addEventListener('pagehide', persistTabs);
    window.addEventListener('beforeunload', persistTabs);

    return () => {
      stopConfigMigration();
      window.removeEventListener('pagehide', persistTabs);
      window.removeEventListener('beforeunload', persistTabs);
    };
  }, []);

  // Desktop OAuth: the CEF host opens the Keycloak auth URL in the system
  // browser, and Keycloak redirects to ateliercode://auth/callback?code=...&
  // state=..., which the OS delivers back into the running page as an
  // 'ateliercode://deep-link' CustomEvent (detail = the full ateliercode:// URL
  // string — see host/src/main.cc dispatch_deeplink_to_page). We parse the
  // callback here and complete the PKCE exchange, mirroring the web oidcCode path
  // above.
  useEffect(() => {
    const onDeepLink = async (e: Event) => {
      const url = (e as CustomEvent<string>).detail;
      if (typeof url !== 'string') return;
      let parsed: URL;
      try {
        parsed = new URL(url);
      } catch {
        return;
      }
      // Only the OAuth callback path is handled here.
      if (parsed.protocol !== 'ateliercode:') return;
      // host + pathname forms 'auth/callback' for ateliercode://auth/callback.
      const isAuthCallback =
        url.startsWith('ateliercode://auth/callback') ||
        (parsed.host === 'auth' && parsed.pathname.replace(/^\/+/, '') === 'callback');
      if (!isAuthCallback) return;

      const code = parsed.searchParams.get('code');
      if (!code) return;

      const { handleCallback } = useAuthStore.getState();
      const success = await handleCallback(code);
      if (success) {
        initServerConnection().catch((err) => {
          logger.debug('App', 'Server connection not available:', err);
        });
      }
    };
    window.addEventListener('ateliercode://deep-link', onDeepLink as EventListener);
    return () => window.removeEventListener('ateliercode://deep-link', onDeepLink as EventListener);
  }, []);

  // When the headless core restarts, the IPC client reconnects and fires
  // `ac:core-reconnected` (CefBackend). A restarted core has an empty in-memory
  // project list until re-queried, so re-fetch to repopulate instead of leaving
  // the sidebar blank.
  useEffect(() => {
    const onReconnect = () => {
      // 1) Re-fetch the project list (a restarted core starts with an empty
      //    in-memory list), THEN 2) adopt every surviving pty-daemon session
      //    across all projects so the workspace comes back the way the user
      //    left it: each project with live terminals shows Active immediately
      //    and its tabs are pre-staged (reattach on open). Adopt is desktop-
      //    only, adopt-only (never spawns), and idempotent (dedupes by PTY id).
      useProjectStore.getState().loadProjects()
        .then(() => adoptAllDaemonSessions(getBackend()))
        .then((ok) => {
          // ok=false means IPC/projects weren't ready yet — one delayed retry.
          if (!ok) {
            setTimeout(() => { void adoptAllDaemonSessions(getBackend()); }, 1500);
          }
        })
        .catch((err) => {
          logger.debug('App', 'Re-fetch/adopt after core reconnect failed:', err);
        });
    };
    window.addEventListener('ac:core-reconnected', onReconnect);
    return () => window.removeEventListener('ac:core-reconnected', onReconnect);
  }, []);

  // When auth state changes in web mode, connect to server
  useEffect(() => {
    if (isWeb() && isAuthenticated) {
      initServerConnection().catch((err) => {
        logger.debug('App', 'Server connection not available:', err);
      });
    }
  }, [isAuthenticated]);

  // Private-beta gate (ticket 102): a signed-in user without a beta role gets
  // the waitlist screen instead of the app. No-op while BETA_GATE is off
  // (isBetaInvited returns true), so current beta access is unaffected.
  const betaBlocked = BETA_GATE && isAuthenticated && !isBetaInvited(user?.roles);
  if (betaBlocked) {
    return (
      <div className="flex flex-col h-screen overflow-hidden">
        <TitleBar />
        <WaitlistScreen />
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <TitleBar />
      <div className={`flex flex-1 overflow-hidden ${sidebarPosition === 'right' ? 'flex-row-reverse' : ''}`}>
        {showSidebar && <ProjectSidebar />}
        <div className="flex-1 min-w-0 overflow-hidden">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/wizard" element={<ProjectWizard />} />
            <Route path="/workspace/:id" element={<DockingWorkspace />} />
            {/* Redirect old settings route to home — settings is now a modal */}
            <Route path="/settings" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>

      {/* Status footer with version info */}
      <StatusFooter />

      {/* Settings Modal (replaces /settings route) */}
      <SettingsModal />

      {/* Floating progress pill for the Project-Maintenance bulk refresh.
          The runner is fire-and-forget so the pill carries the UX after
          the user closes Settings; clicking jumps back to that section. */}
      <BulkRefreshPill />

      {/* Add Project Dialog */}
      <AddProjectDialog />

      {/* Auth Dialog (for web mode) */}
      <AuthDialog />

      {/* RDP Consent Dialog (host: shown when client requests RDP) — hidden until RDP ships */}
      {RDP_ENABLED && pendingConsent && (
        <RdpConsentDialog
          requesterName={pendingConsent.requesterName}
          onResult={resolveConsent}
        />
      )}

      {/* Toast notifications */}
      <ToastContainer />

      {/* Terminal completion notifier (idle detection → sound + toast + browser notification) */}
      <TerminalCompletionNotifier />
    </div>
  );
}

function App() {
  // Popout windows load the SPA with `?popout=<kind>&projectId=<id>`. We
  // detect that at boot and render a stripped-down shell instead of the
  // full workspace — no sidebar, no router, no title bar.
  const popoutCtx = getPopoutContext();
  if (popoutCtx.isPopout) {
    return (
      <ErrorBoundary>
        <PopoutShell />
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
